import { useEffect, useRef, useState } from 'react';
import type { WalletDocumentResponse } from '@shared/types/api';
import { useVoiceRecorder } from '@shared/hooks/use-voice-recorder';
import { EmojiPicker } from './emoji-picker';

export type ReplyTarget = {
  id: string;
  body: string | null;
  senderName: string;
};

type Props = {
  disabled?: boolean;
  placeholder?: string;
  onSend: (
    text: string,
    file: File | null,
    walletDocs: WalletDocumentResponse[],
    metadata?: Record<string, unknown>,
  ) => void;
  onTypingChange: (isTyping: boolean) => void;
  replyTarget: ReplyTarget | null;
  onClearReply: () => void;
  onOpenWallet?: () => void;
  selectedWalletDocs?: WalletDocumentResponse[];
  onClearWalletDocs?: () => void;
  onRemoveWalletDoc?: (id: string) => void;
};

export function Composer({
  disabled,
  placeholder,
  onSend,
  onTypingChange,
  replyTarget,
  onClearReply,
  onOpenWallet,
  selectedWalletDocs,
  onClearWalletDocs,
  onRemoveWalletDoc,
}: Props) {
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const typingRef = useRef<{ typing: boolean; timer: number | null }>({
    typing: false,
    timer: null,
  });

  const {
    isRecording,
    recordingDuration,
    waveformLevels,
    error: recordingError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder();

  async function handleSendVoice() {
    const result = await stopRecording();
    if (!result) return;
    onSend('', result.file, [], {
      isVoiceNote: true,
      durationSeconds: result.durationSeconds,
    });
  }

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 144)}px`;
  }, [text]);

  useEffect(() => {
    if (!disabled) {
      taRef.current?.focus();
    }
  }, [disabled]);

  useEffect(() => {
    if (replyTarget) taRef.current?.focus();
  }, [replyTarget]);

  useEffect(() => {
    async function checkSharedFiles() {
      try {
        const cache = await caches.open('share-target-files');
        const keys = await cache.keys();
        if (keys.length > 0) {
          const res = await cache.match(keys[0]);
          if (res) {
            const blob = await res.blob();
            const fileName = decodeURIComponent(res.headers.get('x-filename') || 'shared-file');
            const type = res.headers.get('x-type') || 'application/octet-stream';
            const file = new File([blob], fileName, { type });
            setSelectedFile(file);
            await cache.delete(keys[0]);
          }
        }
      } catch (err) {
        console.error('Failed to check shared files:', err);
      }
    }
    checkSharedFiles();
  }, []);

  function flushTyping(is: boolean) {
    if (typingRef.current.typing === is) return;
    typingRef.current.typing = is;
    onTypingChange(is);
  }

  function onChange(v: string) {
    setText(v);
    if (v.trim().length > 0) {
      flushTyping(true);
      if (typingRef.current.timer) window.clearTimeout(typingRef.current.timer);
      typingRef.current.timer = window.setTimeout(() => flushTyping(false), 2_500);
    } else {
      flushTyping(false);
    }
  }

  function submit() {
    const body = text.trim();
    const walletDocs = selectedWalletDocs || [];
    if (!body && !selectedFile && walletDocs.length === 0) return;
    flushTyping(false);
    onSend(body, selectedFile, walletDocs);
    setText('');
    setSelectedFile(null);
    onClearWalletDocs?.();
  }

  function insertNewline() {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = ta.value;
    const nextText = value.substring(0, start) + '\n' + value.substring(end);
    onChange(nextText);
    setTimeout(() => {
      if (taRef.current) {
        taRef.current.selectionStart = taRef.current.selectionEnd = start + 1;
      }
    }, 0);
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.key === 'Control' && e.altKey) || (e.key === 'Alt' && e.ctrlKey)) {
      e.preventDefault();
      insertNewline();
    } else if (e.key === 'Enter') {
      if (e.ctrlKey && e.altKey) {
        e.preventDefault();
      } else if (e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    } else if (e.key === 'Escape' && replyTarget) {
      onClearReply();
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      onClearWalletDocs?.();
    }
    e.target.value = '';
  }

  return (
    <div className="bg-[#efeae2] pb-2 sm:pb-3 px-2 sm:px-3 shrink-0">
      {replyTarget ? (
        <div className="mx-1 mt-1 flex items-center gap-2 rounded-t-xl bg-white border-l-4 border-[#008665] px-3.5 py-2.5 text-xs shadow-sm">
          <div className="flex-1 truncate">
            <span className="font-semibold text-[#008665]">
              Replying to {replyTarget.senderName}
            </span>
            <span className="ml-2 text-gray-600">{replyTarget.body ?? '(attachment)'}</span>
          </div>
          <button
            type="button"
            onClick={onClearReply}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
      ) : null}

      {selectedFile ? (
        <div className="mx-1 mt-1 flex items-center gap-2 rounded-t-xl bg-white border-l-4 border-brand-500 px-3.5 py-2.5 text-xs shadow-sm">
          <div className="flex-1 truncate flex items-center gap-1.5">
            <span className="font-semibold text-brand-800">Attachment:</span>
            <span className="text-gray-600 truncate">{selectedFile.name}</span>
            <span className="text-[10px] text-gray-400">
              ({(selectedFile.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <button
            type="button"
            onClick={() => setSelectedFile(null)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
      ) : null}

      {selectedWalletDocs && selectedWalletDocs.length > 0 ? (
        <div className="mx-1 mt-1 flex flex-col gap-1.5 rounded-t-xl bg-white border-l-4 border-emerald-600 px-3.5 py-2.5 text-xs shadow-sm">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-emerald-800">
              Staged Wallet Documents ({selectedWalletDocs.length})
            </span>
            <button
              type="button"
              onClick={onClearWalletDocs}
              className="text-[11px] text-gray-500 hover:text-red-600 font-medium"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {selectedWalletDocs.map((doc) => (
              <div
                key={doc.id}
                className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-900 px-2 py-1 rounded-md text-[11px] font-medium"
              >
                <span className="font-bold text-[10px] text-emerald-700 uppercase">
                  {doc.documentType?.slice(0, 3) || 'DOC'}
                </span>
                <span className="truncate max-w-[150px] font-semibold">{doc.documentName}</span>
                <button
                  type="button"
                  onClick={() => onRemoveWalletDoc?.(doc.id)}
                  className="text-emerald-700 hover:text-red-600 font-bold ml-1"
                  title="Remove document"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {recordingError ? (
        <div className="flex items-center justify-between bg-red-50 px-4 py-2 text-xs text-red-700 border border-red-200 rounded-lg mb-2 shadow-2xs">
          <span>{recordingError}</span>
          <button
            type="button"
            onClick={cancelRecording}
            className="text-red-500 hover:text-red-700 font-bold ml-2"
          >
            ✕
          </button>
        </div>
      ) : null}

      <div className="relative flex items-end gap-2 mt-2">
        {isRecording ? (
          /* Voice recording active container */
          <div className="flex-1 min-w-0 flex items-center justify-between min-h-[46px] bg-red-50/80 border border-red-200/90 rounded-[24px] px-3.5 py-1.5 shadow-2xs animate-in fade-in duration-150">
            {/* Live recording indicator & timer */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
              </span>
              <span className="text-xs font-mono font-semibold text-red-700 tracking-tight">
                {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
              </span>
            </div>

            {/* Live dynamic waveform equalizer bars */}
            <div className="flex-1 max-w-[200px] sm:max-w-[280px] mx-2.5 h-6 flex items-center justify-center gap-[2.5px] overflow-hidden">
              {waveformLevels.length === 0 ? (
                <span className="text-[11px] text-gray-400 italic">Listening…</span>
              ) : (
                waveformLevels.map((lvl, i) => (
                  <span
                    key={i}
                    className="w-[2.5px] sm:w-[3px] rounded-full bg-red-500 transition-all duration-75"
                    style={{
                      height: `${Math.max(4, Math.round(lvl * 24))}px`,
                    }}
                  />
                ))
              )}
            </div>

            {/* Cancel (Trash) button */}
            <button
              type="button"
              onClick={cancelRecording}
              className="p-1.5 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100/70 transition-colors shrink-0"
              title="Discard recording"
              aria-label="Discard recording"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          </div>
        ) : (
          /* White input pill */
          <div className="flex-1 min-w-0 flex items-end bg-white rounded-[24px] border border-gray-200/80 shadow-sm px-1.5 py-1 min-h-[40px] relative">
            <button
              type="button"
              disabled={disabled}
              onClick={() => setShowEmoji((v) => !v)}
              className="p-2 text-gray-500 hover:text-gray-600 shrink-0 rounded-full hover:bg-gray-100/50 disabled:opacity-50"
              aria-label="Add emoji"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                aria-hidden
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            </button>

            {/* Desktop Attachment Button: renders next to the emoji button on desktop */}
            <button
              type="button"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
              className="hidden md:block p-2 text-gray-500 hover:text-gray-600 shrink-0 rounded-full hover:bg-gray-100/50 disabled:opacity-50"
              aria-label="Attach file"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                aria-hidden
              >
                <path d="M21.44 11.05L12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>

            {/* Document Wallet Button */}
            {onOpenWallet ? (
              <button
                type="button"
                disabled={disabled}
                onClick={onOpenWallet}
                className="p-2 text-emerald-700 hover:text-emerald-800 shrink-0 rounded-full hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                title="Open Document Wallet"
                aria-label="Open Document Wallet"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden
                >
                  <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  <path d="M9 12h6m-3-3v6" />
                </svg>
              </button>
            ) : null}

            {showEmoji ? (
              <EmojiPicker
                onPick={(e) => {
                  setText((t) => t + e);
                  taRef.current?.focus();
                }}
                onClose={() => setShowEmoji(false)}
              />
            ) : null}

            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={placeholder ?? (disabled ? 'This chat is closed' : 'Type a message…')}
              disabled={disabled}
              className="flex-1 min-w-0 resize-none border-0 bg-transparent py-2 px-2 focus:ring-0 focus:outline-none text-[15px] max-h-[120px] leading-5 text-gray-900 placeholder:text-gray-400 disabled:text-gray-400"
              style={{ outline: 'none', boxShadow: 'none' }}
            />

            {/* Mobile Attachment Button: renders on the right side of textarea on mobile */}
            <button
              type="button"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
              className="md:hidden p-2 text-gray-500 hover:text-gray-600 shrink-0 rounded-full hover:bg-gray-100/50 disabled:opacity-50"
              aria-label="Attach file"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                aria-hidden
              >
                <path d="M21.44 11.05L12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <input ref={fileRef} type="file" hidden onChange={onFileChange} />
          </div>
        )}

        {/* Circular Action Button: Voice Send vs Microphone vs Message Send */}
        {isRecording ? (
          <button
            type="button"
            onClick={handleSendVoice}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-[#008665] text-white hover:bg-[#006f53] active:bg-[#005c45] transition-all transform hover:scale-105 shadow-sm shrink-0"
            aria-label="Send voice message"
            title="Send voice message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="ml-1"
            >
              <path d="M1.101 21.757L23.8 12.028L1.101 2.3l0.011 7.912 13.623 1.816-13.623 1.817 0.01 7.912z" />
            </svg>
          </button>
        ) : !text.trim() && !selectedFile && (!selectedWalletDocs || selectedWalletDocs.length === 0) ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-[#008665] text-white hover:bg-[#006f53] active:bg-[#005c45] transition-all transform hover:scale-105 shadow-sm shrink-0 disabled:opacity-50 disabled:bg-gray-300 disabled:text-gray-500"
            aria-label="Record voice message"
            title="Record voice message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="22" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={disabled}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-[#008665] text-white hover:bg-[#006f53] active:bg-[#005c45] transition-colors shadow-sm shrink-0 disabled:opacity-50 disabled:bg-gray-300 disabled:text-gray-500"
            aria-label="Send message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="ml-1"
            >
              <path d="M1.101 21.757L23.8 12.028L1.101 2.3l0.011 7.912 13.623 1.816-13.623 1.817 0.01 7.912z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
